import React, { useState } from 'react';
import { Loader2, Upload, Trash2 } from 'lucide-react';
import api, { mediaUrl, uploadImage, errText } from '../lib/api';
import RichTextEditor from './RichTextEditor';
import { CENTER_TYPES, PROGRAM_OPTIONS, DAYS, defaultHours } from '../lib/centerOptions';
import { useToast } from '../hooks/use-toast';

const BLANK = {
  name: '', type: 'ashram', short_description: '', description: '', cover_image: '', gallery: [],
  location: { address: '', city: '', state: '', country: '', pincode: '', google_maps_url: '' },
  contact_person: '', contact_email: '', contact_phone: '', contact_website: '',
  social: { website: '', facebook: '', instagram: '', youtube: '', dr_meditation: '' },
  year_established: '', programs_offered: [], facilities: [],
  initiation_lineage: '', neo_sannyas_based: false, affiliated_with_osho: false,
  hours: defaultHours(),
};

const CenterForm = ({ center, onDone, onCancel }) => {
  const { toast } = useToast();
  const init = center ? {
    ...BLANK, ...center,
    location: { ...BLANK.location, ...(center.location || {}) },
    social: { ...BLANK.social, ...(center.social || {}) },
    year_established: center.year_established ?? '',
    hours: (center.hours && center.hours.length) ? DAYS.map(([day]) => center.hours.find((h) => h.day === day) || { day, open: '09:00', close: '18:00', closed: false }) : defaultHours(),
  } : BLANK;
  const [f, setF] = useState(init);
  const [saving, setSaving] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setLoc = (k, v) => setF((p) => ({ ...p, location: { ...p.location, [k]: v } }));
  const setSoc = (k, v) => setF((p) => ({ ...p, social: { ...p.social, [k]: v } }));
  const setDay = (i, patch) => setF((p) => ({ ...p, hours: p.hours.map((h, idx) => (idx === i ? { ...h, ...patch } : h)) }));
  const toggleProgram = (name) => set('programs_offered',
    f.programs_offered.includes(name) ? f.programs_offered.filter((x) => x !== name) : [...f.programs_offered, name]);

  const upload = async (file, cb) => {
    try { const url = await uploadImage(file); cb(url); toast({ title: 'Image uploaded' }); }
    catch (e) { console.error('Upload failed:', e); toast({ title: 'Upload failed', description: errText(e, 'Please try a different image.') }); }
  };

  const uploadGallery = async (files) => {
    setGalleryUploading(true);
    try {
      const urls = [];
      for (const file of Array.from(files)) {
        urls.push(await uploadImage(file));
      }
      setF((p) => ({ ...p, gallery: [...(p.gallery || []), ...urls] }));
      toast({ title: `${urls.length} image${urls.length > 1 ? 's' : ''} added` });
    } catch { toast({ title: 'Some images failed to upload' }); }
    finally { setGalleryUploading(false); }
  };

  const save = async () => {
    if (!f.name) return toast({ title: 'Center name is required' });
    if (!f.location.address) return toast({ title: 'Address is required' });
    if (!f.contact_phone) return toast({ title: 'Phone is required' });
    setSaving(true);
    const payload = {
      ...f,
      year_established: f.year_established ? parseInt(f.year_established, 10) : null,
      facilities: Array.isArray(f.facilities) ? f.facilities : [],
    };
    ['id', 'slug', 'status', 'is_verified', 'personally_known', 'rating', 'coordinator_ids',
      'facilitator_ids', 'created_by', 'created_by_role', 'created_at', 'updated_at', 'rejection_reason']
      .forEach((k) => delete payload[k]);
    try {
      if (center) await api.patch(`/centers/${center.id}`, payload);
      else await api.post('/centers', payload);
      toast({ title: center ? 'Center updated' : 'Center submitted for review' });
      onDone();
    } catch (err) { toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Try again.' }); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="center-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <Txt label="Center Name*" value={f.name} onChange={(e) => set('name', e.target.value)} testid="center-name" />
        <Sel label="Center Type" value={f.type} onChange={(e) => set('type', e.target.value)} options={CENTER_TYPES} testid="center-type" />
      </div>

      <ImageField label="Main Image" url={f.cover_image} onUpload={(file) => upload(file, (u) => set('cover_image', u))} onClear={() => set('cover_image', '')} />

      <div>
        <span className="block text-xs uppercase tracking-[0.15em] text-mahogany/80 mb-1.5">Image Gallery</span>
        <div className="flex flex-wrap items-center gap-2">
          {(f.gallery || []).map((g, i) => (
            <div key={i} className="relative">
              <img src={mediaUrl(g)} alt="" className="w-16 h-16 rounded-lg object-cover" />
              <button type="button" onClick={() => set('gallery', f.gallery.filter((_, idx) => idx !== i))} className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-5 h-5 text-xs" data-testid={`gallery-remove-${i}`}>×</button>
            </div>
          ))}
          <label className="w-16 h-16 rounded-lg bg-rose-soft/60 flex items-center justify-center cursor-pointer hover:bg-rose-soft" data-testid="gallery-upload">
            {galleryUploading ? <Loader2 className="w-4 h-4 animate-spin text-mahogany" /> : <Upload className="w-4 h-4 text-mahogany/60" />}
            <input type="file" accept="image/*,.heic,.heif" multiple className="hidden" onChange={(e) => { if (e.target.files.length) { uploadGallery(e.target.files); e.target.value = ''; } }} />
          </label>
        </div>
        <p className="text-xs text-mahogany/60 mt-1.5">Select multiple images at once to add them to the gallery.</p>
      </div>

      <Txt label="Short Description" value={f.short_description} onChange={(e) => set('short_description', e.target.value)} testid="center-short-desc" />
      <div>
        <span className="block text-xs uppercase tracking-[0.15em] text-mahogany/80 mb-1.5">Long Description</span>
        <RichTextEditor value={f.description} onChange={(html) => set('description', html)} />
      </div>

      <div className="p-5 rounded-2xl bg-rose-soft/30 border border-rose/40 space-y-3">
        <div className="font-serif-display text-xl text-burgundy">Location</div>
        <Txt label="Address*" value={f.location.address} onChange={(e) => setLoc('address', e.target.value)} testid="center-address" />
        <div className="grid sm:grid-cols-3 gap-3">
          <Txt label="City" value={f.location.city} onChange={(e) => setLoc('city', e.target.value)} />
          <Txt label="State" value={f.location.state} onChange={(e) => setLoc('state', e.target.value)} />
          <Txt label="Country" value={f.location.country} onChange={(e) => setLoc('country', e.target.value)} testid="center-country" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Txt label="Zipcode" value={f.location.pincode} onChange={(e) => setLoc('pincode', e.target.value)} />
          <Txt label="Google Maps URL" value={f.location.google_maps_url} onChange={(e) => setLoc('google_maps_url', e.target.value)} />
        </div>
      </div>

      <div className="p-5 rounded-2xl bg-cream-2/60 border border-rose/40 space-y-3">
        <div className="font-serif-display text-xl text-burgundy">Contact</div>
        <div className="grid sm:grid-cols-3 gap-3">
          <Txt label="Contact Person Name" value={f.contact_person} onChange={(e) => set('contact_person', e.target.value)} />
          <Txt label="Email Address" type="email" value={f.contact_email} onChange={(e) => set('contact_email', e.target.value)} />
          <Txt label="Phone*" value={f.contact_phone} onChange={(e) => set('contact_phone', e.target.value)} testid="center-phone" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Txt label="Website URL" value={f.social.website} onChange={(e) => setSoc('website', e.target.value)} />
          <Txt label="Facebook URL" value={f.social.facebook} onChange={(e) => setSoc('facebook', e.target.value)} />
          <Txt label="Instagram URL" value={f.social.instagram} onChange={(e) => setSoc('instagram', e.target.value)} />
          <Txt label="YouTube URL" value={f.social.youtube} onChange={(e) => setSoc('youtube', e.target.value)} />
          <Txt label="Dr. Meditation URL" value={f.social.dr_meditation} onChange={(e) => setSoc('dr_meditation', e.target.value)} />
          <Txt label="Year Established" type="number" value={f.year_established} onChange={(e) => set('year_established', e.target.value)} />
        </div>
      </div>

      <div className="p-5 rounded-2xl bg-cream-2/60 border border-rose/40 space-y-2" data-testid="center-hours">
        <div className="font-serif-display text-xl text-burgundy">Hours of Operation</div>
        <p className="text-xs text-mahogany/70">Set opening hours for each day, or mark a day as closed.</p>
        {f.hours.map((h, i) => (
          <div key={h.day} className="flex flex-wrap items-center gap-3 py-1">
            <span className="w-28 text-sm text-burgundy capitalize">{DAYS[i][1]}</span>
            <label className="flex items-center gap-1.5 text-sm text-mahogany/80">
              <input type="checkbox" checked={h.closed} onChange={(e) => setDay(i, { closed: e.target.checked })} data-testid={`hours-closed-${h.day}`} />
              Closed
            </label>
            {!h.closed && (
              <div className="flex items-center gap-2">
                <input type="time" value={h.open || ''} onChange={(e) => setDay(i, { open: e.target.value })} data-testid={`hours-open-${h.day}`}
                  className="bg-white/70 rounded-lg border border-rose/50 px-2 py-1 text-burgundy text-sm focus:outline-none" />
                <span className="text-mahogany/60 text-sm">to</span>
                <input type="time" value={h.close || ''} onChange={(e) => setDay(i, { close: e.target.value })} data-testid={`hours-close-${h.day}`}
                  className="bg-white/70 rounded-lg border border-rose/50 px-2 py-1 text-burgundy text-sm focus:outline-none" />
              </div>
            )}
          </div>
        ))}
      </div>

      <div>
        <span className="block text-xs uppercase tracking-[0.15em] text-mahogany/80 mb-2">Programs Offered</span>
        <div className="flex flex-wrap gap-2">
          {PROGRAM_OPTIONS.map((p) => (
            <button key={p} type="button" onClick={() => toggleProgram(p)}
              data-testid={`program-chip-${p.toLowerCase().replace(/\s+/g, '-')}`}
              className={`text-sm px-4 py-1.5 rounded-full border transition-colors ${f.programs_offered.includes(p) ? 'bg-mahogany text-cream border-mahogany' : 'bg-white/70 text-mahogany/80 border-rose/50 hover:bg-rose-soft/40'}`}>
              {p}
            </button>
          ))}
        </div>
      </div>

      <Txt label="Facilities (comma separated)" value={(f.facilities || []).join(', ')} onChange={(e) => set('facilities', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} />
      <Area label="Initiation Lineage Details" rows={2} value={f.initiation_lineage} onChange={(e) => set('initiation_lineage', e.target.value)} />

      <div className="flex flex-wrap gap-6">
        <label className="flex items-center gap-2 text-sm text-burgundy">
          <input type="checkbox" checked={f.neo_sannyas_based} onChange={(e) => set('neo_sannyas_based', e.target.checked)} data-testid="neo-sannyas-toggle" />
          Neo-Sannyas Based
        </label>
        <label className="flex items-center gap-2 text-sm text-burgundy">
          <input type="checkbox" checked={f.affiliated_with_osho} onChange={(e) => set('affiliated_with_osho', e.target.checked)} data-testid="affiliated-osho-toggle" />
          Affiliated with Osho Teachings
        </label>
      </div>

      <div className="flex gap-3 pt-2">
        <button onClick={save} disabled={saving} className="pill-btn justify-center flex-1" data-testid="center-save-btn">{saving && <Loader2 className="w-4 h-4 animate-spin" />} {center ? 'Save Changes' : 'Submit for Review'}</button>
        <button onClick={onCancel} className="pill-btn-outline" data-testid="center-cancel-btn">Cancel</button>
      </div>
    </div>
  );
};

const Txt = ({ label, testid, ...p }) => (
  <label className="block"><span className="block text-xs uppercase tracking-[0.15em] text-mahogany/80 mb-1.5">{label}</span>
    <input {...p} data-testid={testid} className="w-full bg-white/70 rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none" /></label>
);
const Area = ({ label, testid, ...p }) => (
  <label className="block"><span className="block text-xs uppercase tracking-[0.15em] text-mahogany/80 mb-1.5">{label}</span>
    <textarea {...p} data-testid={testid} className="w-full bg-white/70 rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none" /></label>
);
const Sel = ({ label, options, testid, ...p }) => (
  <label className="block"><span className="block text-xs uppercase tracking-[0.15em] text-mahogany/80 mb-1.5">{label}</span>
    <select {...p} data-testid={testid} className="w-full bg-white/70 rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none">{options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
);
const ImageField = ({ label, url, onUpload, onClear }) => (
  <div><span className="block text-xs uppercase tracking-[0.15em] text-mahogany/80 mb-1.5">{label}</span>
    <div className="flex items-center gap-2">
      {url ? <img src={mediaUrl(url)} alt="" className="w-16 h-16 rounded-lg object-cover" /> : <div className="w-16 h-16 rounded-lg bg-rose-soft/60 flex items-center justify-center"><Upload className="w-4 h-4 text-mahogany/60" /></div>}
      <label className="text-xs pill-btn-outline cursor-pointer py-1.5">Upload<input type="file" accept="image/*,.heic,.heif" className="hidden" onChange={(e) => e.target.files[0] && onUpload(e.target.files[0])} /></label>
      {url && <button onClick={onClear} className="text-red-500"><Trash2 className="w-4 h-4" /></button>}
    </div>
  </div>
);

export default CenterForm;
